import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, yen, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { ProductGrid } from "../components/ProductGrid";
import { Cart } from "../components/Cart";
import { PaymentDialog } from "../components/PaymentDialog";
import { ReceiptDialog } from "../components/ReceiptDialog";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { ArrowLeft, Plus, Users, Trash2 } from "lucide-react";

export default function Meja() {
  const { t } = useI18n();
  const [tables, setTables] = useState([]);
  const [menus, setMenus] = useState([]);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newTable, setNewTable] = useState("");

  const load = useCallback(async () => {
    const [t, m] = await Promise.all([
      api.get("/tables"),
      api.get("/products", { params: { kind: "menu" } }),
    ]);
    setTables(t.data);
    setMenus(m.data);
    setActive((prev) => (prev ? t.data.find((x) => x.id === prev.id) || prev : null));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const openTable = (t) => {
    setActive(t);
    setItems([]);
    setDiscount("");
  };

  const pick = (p) =>
    setItems((prev) => {
      const f = prev.find((i) => i.product_id === p.id);
      if (f)
        return prev.map((i) => (i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, { product_id: p.id, name: p.name, price: p.price, qty: 1, unit: p.unit }];
    });

  const setQty = (id, qty) =>
    setItems((prev) =>
      qty <= 0
        ? prev.filter((i) => i.product_id !== id)
        : prev.map((i) => (i.product_id === id ? { ...i, qty } : i))
    );

  const sendOrder = async () => {
    setBusy(true);
    try {
      await api.post("/orders", {
        order_type: "dine_in",
        table_id: active.id,
        items,
        discount: 0,
      });
      setItems([]);
      await load();
      toast.success("Pesanan ditambahkan ke meja");
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan pesanan"));
    } finally {
      setBusy(false);
    }
  };

  const bill = active?.open_order;
  const billTotal = bill ? Math.max(bill.subtotal - (Number(discount) || 0), 0) : 0;

  const payBill = async (method, paidAmount) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${bill.id}/pay`, {
        payment_method: method,
        paid_amount: paidAmount || billTotal,
        discount: Number(discount) || 0,
      });
      setReceipt(data);
      setPayOpen(false);
      setDiscount("");
      setItems([]);
      setActive(null);
      await load();
      toast.success("Meja selesai dibayar");
    } catch (e) {
      toast.error(errMsg(e, "Gagal membayar"));
    } finally {
      setBusy(false);
    }
  };

  const addTable = async () => {
    if (!newTable.trim()) return;
    await api.post("/tables", { name: newTable.trim(), seats: 4 });
    setNewTable("");
    setAddOpen(false);
    load();
    toast.success("Meja ditambahkan");
  };

  if (!active)
    return (
      <div className="p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-semibold">{t("meja_title")}</h1>
            <p className="text-sm text-muted-foreground">{t("meja_sub")}</p>
          </div>
          <Button data-testid="add-table-btn" onClick={() => setAddOpen(true)} variant="outline">
            <Plus size={16} className="mr-1" /> {t("add_table")}
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 lg:gap-6">
          {tables.map((tb) => {
            const occupied = !!tb.open_order;
            return (
              <button
                key={tb.id}
                data-testid={`table-card-${tb.id}`}
                onClick={() => openTable(tb)}
                className={`aspect-square rounded-2xl border p-4 flex flex-col justify-between text-left transition-all duration-200 hover:-translate-y-1 hover:shadow-md active:scale-95 ${
                  occupied
                    ? "bg-primary/10 border-primary/30"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      occupied ? "bg-primary" : "bg-emerald-500"
                    }`}
                  />
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Users size={12} /> {tb.seats}
                  </span>
                </div>
                <div>
                  <p className="font-display text-xl font-semibold">{tb.name}</p>
                  <p className="text-xs text-muted-foreground num">
                    {occupied ? yen(tb.open_order.subtotal) : t("empty_table")}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-display">{t("add_table")}</DialogTitle>
            </DialogHeader>
            <Input
              data-testid="new-table-name-input"
              placeholder={t("add_table")}
              value={newTable}
              onChange={(e) => setNewTable(e.target.value)}
            />
            <Button data-testid="save-table-btn" onClick={addTable}>
              {t("save")}
            </Button>
          </DialogContent>
        </Dialog>
        <ReceiptDialog order={receipt} onClose={() => setReceipt(null)} />
      </div>
    );

  return (
    <div className="flex flex-col xl:flex-row min-h-screen">
      <div className="flex-1 p-6 lg:p-8">
        <button
          data-testid="back-to-tables-btn"
          onClick={() => setActive(null)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft size={16} /> {t("all_tables")}
        </button>
        <h1 className="font-display text-3xl font-semibold mb-1">{active.name}</h1>
        <p className="text-sm text-muted-foreground mb-6">{t("meja_menu_sub")}</p>
        <ProductGrid products={menus} onPick={pick} />
      </div>
      <div className="xl:w-[380px] xl:sticky xl:top-0 xl:h-screen bg-white border-t xl:border-t-0 xl:border-l border-gray-200 flex flex-col">
        <Cart
          items={items}
          onQty={setQty}
          onRemove={(id) => setQty(id, 0)}
          discount={"0"}
          setDiscount={() => {}}
          onSubmit={sendOrder}
          submitLabel={t("send_to_table")}
          title={t("new_order")}
          subtitle={active.name}
          busy={busy}
        />
        {bill && (
          <div className="border-t border-gray-200 p-6 space-y-3" data-testid="table-bill">
            <p className="font-display font-semibold">{t("bill")} ({bill.order_no})</p>
            <div className="space-y-1 max-h-40 overflow-y-auto text-sm">
              {bill.items.map((it, i) => (
                <div key={it.product_id || `${it.name}-${i}`} className="flex justify-between">
                  <span>
                    {it.name} <span className="text-muted-foreground">x{it.qty}</span>
                  </span>
                  <span className="num">{yen(it.price * it.qty)}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">{t("discount")}</span>
              <Input
                data-testid="bill-discount-input"
                inputMode="numeric"
                value={discount}
                placeholder="0"
                onChange={(e) => setDiscount(e.target.value.replace(/[^0-9]/g, ""))}
                className="h-9 w-28 text-right num"
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="font-display font-semibold">{t("total")}</span>
              <span className="num text-2xl font-semibold" data-testid="bill-total">
                {yen(billTotal)}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                data-testid="cancel-bill-btn"
                onClick={async () => {
                  await api.delete(`/orders/${bill.id}`);
                  await load();
                  toast.success("Pesanan meja dibatalkan");
                }}
              >
                <Trash2 size={16} />
              </Button>
              <Button
                className="flex-1 h-12"
                data-testid="pay-bill-btn"
                onClick={() => setPayOpen(true)}
              >
                {t("pay_bill")}
              </Button>
            </div>
          </div>
        )}
      </div>
      <PaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        total={billTotal}
        onConfirm={payBill}
        busy={busy}
      />
      <ReceiptDialog order={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}
